import { Scrollable } from "@/components/Scrollable/Scrollable";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/input";
import { Tabs, TabsDummyButton } from "@/components/ui/tabs";
import { EXPERT_TYPE_LABELS } from "@/consts/experts";
import { cn } from "@/lib/utils";
import { Expert } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBellConcierge } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useMemo, useState } from "react";
import { ExpertItems, ExpertItemsProps } from "./ExpertItems";
import { useExpertSelection } from "./ExpertSelectionContext";

type ExpertBrowserProps = Pick<
  ExpertItemsProps,
  | "onExpertSelection"
  | "variant"
  | "selectedExpertUuids"
  | "previousSelectedExpertUuids"
  | "max"
  | "disabled"
> & {
  source: "availableExperts" | "groupExperts";
};

const EXPERT_TYPE_LABELS_EXTENDED = {
  all: "All",
  ...EXPERT_TYPE_LABELS,
  custom: "Custom",
} as const;

type ExpertTypeExtended = keyof typeof EXPERT_TYPE_LABELS_EXTENDED;

// All EXPERT_TYPE_LABELS_WITH_CUSTOM + "Custom" - "Interviewer"
const TABS = Object.entries(EXPERT_TYPE_LABELS_EXTENDED).filter(
  ([type]) => type !== "interviewer",
) as [ExpertTypeExtended, string][];

const groupByExpertType = (experts: Expert[], groupUuid: string | null) => {
  const groups: Record<ExpertTypeExtended, Expert[]> = Object.keys(
    EXPERT_TYPE_LABELS_EXTENDED,
  ).reduce(
    (acc, key) => {
      acc[key as ExpertTypeExtended] = [];
      return acc;
    },
    {} as Record<ExpertTypeExtended, Expert[]>,
  );
  for (let expert of experts) {
    if (expert.group?.uuid === groupUuid) {
      groups["custom"].push(expert);
    }

    const type = expert.expertType;
    if (!(type in groups)) {
      groups[type] = [];
    }
    groups[type].push(expert);
    groups["all"].push(expert);
  }
  return groups;
};

const filterGroupBySearchText = (
  groups: Record<ExpertTypeExtended, Expert[]>,
  searchText: string,
) => {
  if (searchText.trim() === "") {
    return groups;
  }
  return Object.fromEntries(
    Object.entries(groups).map(([type, experts]) => [
      type,
      experts.filter((expert) =>
        expert.name.toLowerCase().includes(searchText.toLowerCase()),
      ),
    ]),
  );
};

export const ExpertBrowser = ({ source, ...rest }: ExpertBrowserProps) => {
  const { availableExperts, groupExperts, groupUuid } = useExpertSelection();
  const experts = source === "groupExperts" ? groupExperts : availableExperts;
  const [searchText, setSearchText] = useState("");
  const [expertTypeFilter, setExpertTypeFilter] =
    useState<ExpertTypeExtended>("all");

  // Allowed experts
  const { unfilteredExpertGroups, filteredExpertGroups } = useMemo(() => {
    const unfilteredExpertGroups = groupByExpertType(experts, groupUuid);
    const filteredExpertGroups = filterGroupBySearchText(
      unfilteredExpertGroups,
      searchText,
    );
    return { unfilteredExpertGroups, filteredExpertGroups };
  }, [experts, groupUuid, searchText]);

  const results = filteredExpertGroups[expertTypeFilter].length;

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-hidden">
      <div className="flex flex-row items-center justify-between gap-4">
        <Tabs
          value={expertTypeFilter}
          onValueChange={(value) => {
            setExpertTypeFilter(value as ExpertTypeExtended);
          }}
        >
          <div className="flex flex-row gap-1">
            {TABS.map(([type, label]) => {
              const experts =
                type in unfilteredExpertGroups
                  ? unfilteredExpertGroups[type]
                  : [];
              // Only show categories that have experts
              if (!experts.length) {
                return null;
              }
              return (
                <TabsDummyButton
                  key={type}
                  isActive={expertTypeFilter === type}
                  onMouseUp={() => {
                    setExpertTypeFilter(type as ExpertTypeExtended);
                  }}
                >
                  {label}
                </TabsDummyButton>
              );
            })}
          </div>
        </Tabs>
        <div className="max-w-80 flex-1">
          <SearchInput
            placeholder="Search expert..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            className="w-full"
            autoFocus
          />
        </div>
      </div>
      {expertTypeFilter === "custom" &&
        filteredExpertGroups.custom.length === 0 && (
          <div className="flex min-h-80 flex-col items-center justify-center gap-4">
            <div className="text-lg">You don't have custom experts</div>
            <Button asChild>
              <a
                href="https://cta-service-cms2.hubspot.com/web-interactives/public/v1/track/redirect?encryptedPayload=AVxigLJ1OsEbyg8N50yfXYgXUud9yUn48kbBZoyNpsyH6Q00x3HvZo%2FOfFdrNZ5vUG%2BOTdoMECc9liGLpKmHPuSSkmpuEE09ct346xwQjVeTtwyONEs%3D&webInteractiveContentId=199945563218&portalId=47610301"
                onClick={(e) => e.stopPropagation()}
                target="_blank"
              >
                <FontAwesomeIcon icon={faBellConcierge} />
                Request Custom Expert
              </a>
            </Button>
          </div>
        )}
      {expertTypeFilter !== "custom" && results === 0 && (
        <div className="flex min-h-80 flex-col items-center justify-center gap-4">
          <div className="text-lg">No experts found</div>
        </div>
      )}
      {results > 0 && (
        <Scrollable
          outerClassName="flex flex-1 flex-col"
          fromColorClassName={
            rest.variant === "list" ? "from-background" : "from-background-1"
          }
        >
          <ul
            className={cn("flex flex-1 flex-row flex-wrap p-0.5", {
              "flex-col gap-[1px]": rest.variant === "list",
              "flex-row gap-1": rest.variant === "cards",
            })}
          >
            <ExpertItems
              experts={filteredExpertGroups[expertTypeFilter]}
              groupUuid={groupUuid}
              {...rest}
            />
          </ul>
        </Scrollable>
      )}
    </div>
  );
};
